'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import styled from 'styled-components';
import {
  mqXLargeMin,
  mqLargeMin,
  mqLargeMax,
} from 'styled/tools/media-queries';
import { InView } from 'react-intersection-observer';
import type {
  ContentImage,
  NutrientsCard,
  NutrientsModule,
} from 'lib/pdp-modules/types';
import NutrientCycles from './nutrient-cycles';

const ProductNutrientsContainer = styled.div`
  padding-bottom: 3.5rem;
  background-color: var(--fill-secondary);

  @media ${mqLargeMin} {
    padding-bottom: 5rem;
  }

  * {
    color: var(--text-primary);
  }

  @media ${mqLargeMin} {
    &.nutrients--compact .nutrients__content {
      margin-left: auto;
    }
  }
`;

const CardWrapper = styled.div`
  padding: 0.75rem 1rem 0;
  display: flex;
  align-items: center;
  overflow: auto;
  scroll-snap-type: x mandatory;
  -webkit-scroll-snap-type: x mandatory;
  overflow-x: auto;
  gap: 1rem;
  scrollbar-width: none;

  @media ${mqLargeMin} {
    margin-left: 0;
    padding: 0.5rem 1rem 0.5rem calc((100vw - 1280px) / 2);
    scroll-snap-type: none;
    -webkit-scroll-snap-type: none;
    cursor: grab;

    &.mini {
      margin: auto;
      padding: 0 5rem;
      max-width: 80rem;
      width: 100%;
      box-sizing: content-box;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &.is-dragging {
    cursor: grabbing;

    * {
      user-select: none;
    }
  }
`;

const NutrientCard = styled.div`
  position: relative;
  backdrop-filter: blur(10.85301399230957px);
  border-radius: 1.5rem;
  border: 1px solid var(--outline-primary-10);
  padding: 1rem 1.5rem;
  width: 20rem;
  flex-shrink: 0;
  min-height: 21.1875rem;
  display: flex;
  flex-direction: column;
  background-color: var(--fill-primary);
  box-sizing: content-box;
  transition: padding 0.05s 0.05s;

  &.selected {
    padding: 1.5rem;
    transition: padding 0.05s;
  }

  @media ${mqLargeMin} {
    scroll-snap-align: start;
    -webkit-scroll-snap-align: start;
  }
`;

const NutrientInfo = styled.p`
  font-size: 14px;
  font-weight: 350;
  line-height: 140%;
  letter-spacing: -0.07px;
`;

const CardTitle = styled.h3`
  margin-bottom: 0.5rem;
  font-size: 24px;
  font-weight: 350;
  line-height: 120%;
  letter-spacing: -0.36px;

  &.mini {
    font-size: 16px;
  }
`;

const CardSubtitle = styled.p`
  margin-bottom: 1rem;
  font-size: 12px;
  font-weight: 400;
  line-height: 110%;
  text-transform: uppercase;
`;

const NutrientVideo = styled.video`
  margin: var(--spacing-16) auto 0;
  width: 294px;
  max-width: 100%;
  min-height: 208px;
  flex: 1 1 0;
  object-fit: cover;
`;

const ContentsWrapper = styled.div`
  margin: auto;
  padding: 3.5rem 1rem 2.5rem;
  max-width: 1280px;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 2.5rem;
  box-sizing: content-box;

  @media ${mqLargeMin} {
    padding: 5rem;
  }

  @media (${mqXLargeMin}) {
    flex-wrap: nowrap;
  }
`;

const NutrientsTitle = styled.h2`
  max-width: 37.5rem;
  font-size: 32px;
  font-weight: 350;
  line-height: 110%;
  text-wrap: unset;
  letter-spacing: -0.96px;

  @media ${mqLargeMin} {
    font-size: 48px;
  }
`;

const NutrientsContent = styled.p`
  max-width: 26.875rem;
`;

const ExclusiveTag = styled.div`
  position: absolute;
  left: 1.5rem;
  top: 0;
  transform: translateY(-50%);
  border-radius: 1rem;
  padding: 0 8px;
  height: 20px;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  background-color: var(--guidance-primary);
`;

const CardColorTag = styled.div`
  margin-bottom: 0.5rem;
  flex-shrink: 0;
  width: 1rem;
  height: 0rem;
  opacity: 0;
  transition: all 0.1s;

  &.tag-visible {
    height: 1rem;
    opacity: 1;
  }

  @media ${mqLargeMax} {
    display: none;
  }
`;

const ChartImageWrapper = styled.div`
  margin: 2.625rem auto 0;
  max-width: 80rem;
  padding: 1rem;
`;

const CardIndicatorWrapper = styled.div`
  margin-top: 2rem;
  padding: 0 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;

  @media ${mqLargeMin} {
    display: none;
  }
`;

const CardIndicator = styled.div`
  border-radius: 0.5rem;
  width: 0.5rem;
  height: 0.5rem;
  background-color: var(--fill-secondary);
  transition: all 0.2s;

  &.selected {
    width: 1rem;
    background-color: var(--fill-brand);
  }
`;

const DRAG_THRESHOLD_PX = 5;

const cardChartImage = (card?: NutrientsCard): ContentImage | null =>
  card?.chartImage?.url ? card.chartImage : null;

type ProductNutrientsProps = {
  productInfo: { shortTitle?: string };
  nutrients: NutrientsModule;
};

const ProductNutrients = ({
  productInfo,
  nutrients,
}: ProductNutrientsProps) => {
  const overviewChartMedia = useMemo(() => {
    const chart = nutrients?.overviewChart;
    return chart?.url ? { url: chart.url, alt: chart.alt ?? '' } : null;
  }, [nutrients]);

  const [selectedTile, setSelectedTile] = useState<number | null>(() =>
    overviewChartMedia != null ? null : 0,
  );
  const [selectedTileImage, setSelectedTileImage] =
    useState<ContentImage | null>(() => cardChartImage(nutrients?.cards?.[0]));
  const [isStripDragging, setIsStripDragging] = useState(false);

  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const dragStripRef = useRef({
    active: false,
    startX: 0,
    startScrollLeft: 0,
    hasDragged: false,
  });

  useEffect(() => {
    if (selectedTile === null) {
      return;
    }
    if (!nutrients?.cards?.[selectedTile]) {
      return;
    }
    const newTileImage = cardChartImage(nutrients.cards[selectedTile]);
    if (newTileImage) {
      setSelectedTileImage(newTileImage);
    }
  }, [selectedTile, nutrients]);

  useEffect(() => {
    if (overviewChartMedia) {
      return;
    }
    if (selectedTile === null) {
      return;
    }
    const tileToPlay = document.querySelector(
      `[nutrient-card="${selectedTile}"]`,
    );
    tileToPlay?.querySelector('video')?.play();
  }, [overviewChartMedia, selectedTile]);

  const goToNewVideo = (videoIndex: number, scrollToVideo = false) => {
    const previousTile =
      selectedTile !== null
        ? document.querySelector(`[nutrient-card="${selectedTile}"]`)
        : null;
    const tileToPlay = document.querySelector(
      `[nutrient-card="${videoIndex}"]`,
    );
    previousTile?.querySelector('video')?.pause();
    tileToPlay?.querySelector('video')?.play();

    if (scrollToVideo) {
      tileToPlay?.scrollIntoView({
        inline: 'center',
        behavior: 'smooth',
        block: 'nearest',
      });
    }

    setSelectedTile(videoIndex);
  };

  const resetToOverviewChart = () => {
    if (!overviewChartMedia || dragStripRef.current.active) {
      return;
    }
    setSelectedTile((current) => {
      if (current !== null) {
        document
          .querySelector(`[nutrient-card="${current}"]`)
          ?.querySelector('video')
          ?.pause();
      }
      return null;
    });
  };

  const handleCardsFocusOut = (e: FocusEvent<HTMLDivElement>) => {
    if (!overviewChartMedia || dragStripRef.current.active) {
      return;
    }
    const next = e.relatedTarget;
    if (next && e.currentTarget.contains(next as Node)) {
      return;
    }
    resetToOverviewChart();
  };

  // if we're on mobile set new visible image as selected
  const checkMobileScrollCards = () => {
    const visibleCards = document.querySelectorAll('.visible[nutrient-card]');
    if (visibleCards.length === 1) {
      const newIndexToView = visibleCards[0].getAttribute('nutrient-card');
      if (newIndexToView === null) {
        return;
      }
      goToNewVideo(parseInt(newIndexToView, 10));
    }
  };

  const checkMobileScrollCardsRef = useRef(checkMobileScrollCards);
  checkMobileScrollCardsRef.current = checkMobileScrollCards;

  const onStripMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) {
      return;
    }
    const el = cardWrapperRef.current;
    if (!el) {
      return;
    }
    dragStripRef.current = {
      active: true,
      startX: e.pageX,
      startScrollLeft: el.scrollLeft,
      hasDragged: false,
    };
    setIsStripDragging(true);
    el.style.scrollSnapType = 'none';
    el.style.setProperty('-webkit-scroll-snap-type', 'none');

    const onMove = (moveEvent: globalThis.MouseEvent) => {
      const state = dragStripRef.current;
      if (!state.active) {
        return;
      }
      const dx = moveEvent.pageX - state.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) {
        state.hasDragged = true;
      }
      el.scrollLeft = state.startScrollLeft - dx;
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragStripRef.current.active = false;
      setIsStripDragging(false);
      el.style.scrollSnapType = '';
      el.style.removeProperty('-webkit-scroll-snap-type');
      checkMobileScrollCardsRef.current();
      dragStripRef.current.hasDragged = false;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  useLayoutEffect(() => {
    if (!nutrients || !productInfo) {
      return;
    }
    const el = cardWrapperRef.current;
    if (!el) {
      return;
    }
    const onScrollEnd = () => checkMobileScrollCardsRef.current();
    el.addEventListener('scrollend', onScrollEnd);
    return () => el.removeEventListener('scrollend', onScrollEnd);
  }, [nutrients, productInfo]);

  if (!nutrients || !productInfo) {
    return null;
  }

  const allCycles = nutrients.cards.reduce<
    Record<string, { label: string; icon?: ContentImage; cardIndex: number }>
  >((cycles, card, cardIndex) => {
    if (card.cycle) {
      cycles[card.cycle] = {
        label: card.cycle,
        icon: card.cycleIcon,
        cardIndex: cardIndex,
      };
    }
    return cycles;
  }, {});

  const displayedChart =
    overviewChartMedia && selectedTile === null
      ? overviewChartMedia
      : selectedTileImage;
  const chartBlock = displayedChart ? (
    <ChartImageWrapper>
      <img src={displayedChart.url} alt={displayedChart.alt} />
    </ChartImageWrapper>
  ) : null;

  return (
    <ProductNutrientsContainer
      className={`${nutrients.cards.length < 4 ? 'nutrients--compact' : ''}`}
    >
      <>
        <ContentsWrapper>
          <NutrientsTitle>{nutrients.title}</NutrientsTitle>
          {nutrients.content ? (
            <NutrientsContent className='nutrients__content'>
              {nutrients.content}
            </NutrientsContent>
          ) : null}
        </ContentsWrapper>
        <CardWrapper
          ref={cardWrapperRef}
          onMouseDown={onStripMouseDown}
          onMouseLeave={overviewChartMedia ? resetToOverviewChart : undefined}
          onBlur={overviewChartMedia ? handleCardsFocusOut : undefined}
          onTouchEnd={checkMobileScrollCards}
          className={`${nutrients.cards.length <= 4 ? 'mini' : ''} ${isStripDragging ? 'is-dragging' : ''}`}
        >
          {nutrients.cards.map((nutrientCard, nutrientCardIndex) => (
            <InView key={`nutrient-card-${nutrientCard._key}`} threshold={0.5}>
              {({ inView, ref }) => (
                <NutrientCard
                  className={`${selectedTile === nutrientCardIndex ? 'selected' : ''} ${inView ? 'visible' : 'not-visible'}`}
                  nutrient-card={nutrientCardIndex}
                  onMouseEnter={() => {
                    if (dragStripRef.current.active) {
                      return;
                    }
                    goToNewVideo(nutrientCardIndex);
                  }}
                  onFocus={() => goToNewVideo(nutrientCardIndex)}
                  ref={ref}
                  tabIndex={0}
                >
                  {nutrientCard.isExclusive === true && (
                    <ExclusiveTag>
                      {productInfo.shortTitle} Exclusive
                    </ExclusiveTag>
                  )}
                  {nutrientCard.isExclusive !== true &&
                    nutrientCard.selectedTagColor && (
                      <CardColorTag
                        style={{
                          backgroundColor: nutrientCard.selectedTagColor,
                        }}
                        className={
                          selectedTile === nutrientCardIndex
                            ? 'tag-visible'
                            : ''
                        }
                      />
                    )}
                  <CardTitle
                    className={`${nutrientCard.title.length > 40 ? 'mini' : ''}`}
                  >
                    {nutrientCard.title}
                  </CardTitle>
                  <NutrientInfo>{nutrientCard.description}</NutrientInfo>
                  <NutrientVideo loop muted playsInline>
                    {(nutrientCard.video?.sources || []).map((source) => (
                      <source
                        key={`nutrient-${nutrientCard._key}-source-${source.url}`}
                        src={source.url}
                      />
                    ))}
                  </NutrientVideo>
                  <CardSubtitle>{nutrientCard.subtitle}</CardSubtitle>
                  <NutrientInfo>{nutrientCard.subcontent}</NutrientInfo>
                </NutrientCard>
              )}
            </InView>
          ))}
        </CardWrapper>
        <CardIndicatorWrapper>
          {nutrients.cards.map((nutrientCard, nutrientCardIndex) => (
            <CardIndicator
              key={`nutrient-indicator-${nutrientCard._key}`}
              className={`${selectedTile === nutrientCardIndex ? 'selected' : ''}`}
            ></CardIndicator>
          ))}
        </CardIndicatorWrapper>
        {Object.values(allCycles).length > 0 && (
          <NutrientCycles
            allCycles={allCycles}
            selectedTile={selectedTile}
            goToNewVideo={goToNewVideo}
          />
        )}
        {chartBlock}
      </>
    </ProductNutrientsContainer>
  );
};

export default ProductNutrients;
